import { createAsyncThunk } from '@reduxjs/toolkit';
import { changePassword } from '@wix/ambassador-iam-authentication-v1-authentication/http';
import { updateIdentity } from '@wix/ambassador-iam-identity-v1-identity/http';
import type { Identity } from '@wix/ambassador-iam-identity-v1-identity/types';
import { withResolvedError } from '@wix/members-area-commons-ts';

import { Interactions } from '../../../constants/interactions';
import type {
  ChangeEmailState,
  ChangePasswordState,
  FlowApi,
  ThunkApiConfig,
  UpdateLoginEmailArgs,
  UpdateLoginPasswordArgs,
} from '../../../types';

const isWrongPasswordError = (err: any): boolean => {
  return err?.details?.applicationError?.code === '-19976';
};

const isEmailAlreadyExistsError = (err: any): boolean => {
  return err?.response?.data?.details?.applicationError?.code === '-19995';
};

const updateLoginEmailRequest = (identity: Identity, flowAPI: FlowApi) => {
  const { httpClient, errorHandler } = flowAPI;

  const requestFn = () =>
    httpClient.request(
      updateIdentity({
        fieldMask: ['email'],
        identity,
      }),
    );

  return withResolvedError(requestFn, errorHandler, {
    errorCodesMap: {},
  });
};

export const updateLoginEmail = createAsyncThunk<
  ChangeEmailState,
  UpdateLoginEmailArgs,
  ThunkApiConfig
>(
  'loginInfo/updateEmail',
  async (
    { newEmail, password },
    { getState, extra: { flowAPI, wixCodeApi } },
  ) => {
    flowAPI.fedops.interactionStarted(Interactions.LoginInfoEmailUpdate);
    const state = getState();
    try {
      // @ts-expect-error
      const identity = await wixCodeApi.members.authentication.login(
        state.member.loginEmail,
        password,
      );

      const identityPayload = {
        id: identity._id,
        revision: identity.revision,
        email: {
          address: newEmail,
        },
      };

      await updateLoginEmailRequest(identityPayload, flowAPI);

      flowAPI.fedops.interactionEnded(Interactions.LoginInfoEmailUpdate);
      return { stage: 'ok', newEmail };
    } catch (err: any) {
      if (isWrongPasswordError(err)) {
        const message = flowAPI.translations.t(
          'app.widget.modals.change-email.password.error-wrong',
        );

        flowAPI.fedops.interactionEnded(Interactions.LoginInfoEmailUpdate);
        return {
          stage: 'fail',
          errors: {
            password: { message },
          },
        };
      }
      if (isEmailAlreadyExistsError(err)) {
        const message = flowAPI.translations.t(
          'app.widget.modals.change-email.new-email.error-email-exists',
        );

        flowAPI.fedops.interactionEnded(Interactions.LoginInfoPasswordUpdate);
        return {
          stage: 'fail',
          errors: {
            newEmail: { message },
          },
        };
      }
      flowAPI.reportError(err);
      return { stage: 'fail' };
    }
  },
);

const changePasswordRequest = (newPassword: string, flowAPI: FlowApi) => {
  const { httpClient, errorHandler } = flowAPI;

  const requestFn = () => httpClient.request(changePassword({ newPassword }));

  return withResolvedError(requestFn, errorHandler, {
    errorCodesMap: {},
  });
};

export const updateLoginPassword = createAsyncThunk<
  ChangePasswordState,
  UpdateLoginPasswordArgs,
  ThunkApiConfig
>(
  'loginInfo/updatePassword',
  async (
    { currentPassword, newPassword },
    { getState, extra: { flowAPI, wixCodeApi } },
  ) => {
    flowAPI.fedops.interactionStarted(Interactions.LoginInfoPasswordUpdate);
    const state = getState();
    try {
      await wixCodeApi.user.login(state.member.loginEmail, currentPassword);

      await changePasswordRequest(newPassword, flowAPI);

      flowAPI.fedops.interactionEnded(Interactions.LoginInfoPasswordUpdate);
      return {
        stage: 'ok',
      };
    } catch (err: any) {
      if (isWrongPasswordError(err)) {
        flowAPI.fedops.interactionEnded(Interactions.LoginInfoPasswordUpdate);
        const message = flowAPI.translations.t(
          'app.widget.modals.change-password.password.error-wrong',
        );

        return {
          stage: 'fail',
          errors: {
            currentPassword: { message },
          },
        };
      }
      flowAPI.reportError(err);
      return { stage: 'fail' };
    }
  },
);

export const recoverPassword = createAsyncThunk<void, void, ThunkApiConfig>(
  'loginInfo/recoverPassword',
  async (_, { rejectWithValue, getState, extra: { wixCodeApi, flowAPI } }) => {
    flowAPI.fedops.interactionStarted(Interactions.LoginInfoRecoverPassword);
    const state = getState();
    try {
      // @ts-expect-error
      await wixCodeApi.members.authentication.sendSetPasswordEmail(
        state.member.loginEmail,
      );
      flowAPI.fedops.interactionEnded(Interactions.LoginInfoRecoverPassword);
    } catch (err: any) {
      flowAPI.reportError(err);
      return rejectWithValue(err);
    }
  },
);

export const loginInfoThunk = {
  updateLoginEmail,
  updateLoginPassword,
  recoverPassword,
};
